import { useEffect, useRef, useState } from 'react';
import * as ForceGraphLib from 'force-graph';
import { Loader2, Filter, BrushCleaning, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { labelColor, labelText } from '../lib/knowledge';
import type { KnowledgeLabelSummary, KnowledgeRecord, KnowledgeRecordDetail } from '../lib/types';

const ForceGraph = (ForceGraphLib as any).default || ForceGraphLib;

// `pending`: a record or relationship a linked document's claims would add, awaiting review.
interface GNode { id: string; label: string; key: string; name: string; pending?: boolean; x?: number; y?: number; }
interface GLink { source: string; target: string; type: string; pending?: boolean; }

// Expand-from-seed graph view. Reuses the graph-DB endpoints: `/knowledge/records/{label}`
// to seed and `/knowledge/records/{label}/{key}` (traverse) to expand on click, so a
// company several sources describe is one node rather than one per source. The
// full node/link set lives in dataRef; the canvas shows the label-filtered subset.
export default function KnowledgeGraph({ onOpenNode }: { onOpenNode?: (label: string, key: string) => void }) {
    const { authFetch } = useAuth();
    const containerRef = useRef<HTMLDivElement>(null);
    const graphRef = useRef<any>(null);
    // The graph is initialised once, so node clicks go through a ref that points at the latest handlers.
    const onNodeClickRef = useRef<(label: string, key: string) => void>(() => {});
    const dataRef = useRef<{ nodes: GNode[]; links: GLink[] }>({ nodes: [], links: [] });
    const fetchedRef = useRef<Set<string>>(new Set());
    const enabledRef = useRef<Set<string>>(new Set());          // enabled node labels ([] = all)

    const [labels, setLabels] = useState<KnowledgeLabelSummary[]>([]);
    const [seedLabel, setSeedLabel] = useState('');
    const [seedNodes, setSeedNodes] = useState<KnowledgeRecord[]>([]);
    const [seedFilter, setSeedFilter] = useState('');
    const [loadingSeeds, setLoadingSeeds] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [, force] = useState(0);
    const rerender = () => force(n => n + 1);

    const nid = (label: string, key: string) => `${label}||${key}`;

    const applyToGraph = () => {
        const enabled = enabledRef.current;
        const nodes = dataRef.current.nodes.filter(n => enabled.size === 0 || enabled.has(n.label));
        const ids = new Set(nodes.map(n => n.id));
        const links = dataRef.current.links.filter(l => ids.has(l.source) && ids.has(l.target));
        // Fresh copies, because force-graph mutates source/target into node objects.
        graphRef.current?.graphData({ nodes: nodes.map(n => ({ ...n })), links: links.map(l => ({ ...l })) });
    };

    const addNode = (label: string, key: string, name: string, pending?: boolean) => {
        const id = nid(label, key);
        const known = dataRef.current.nodes.find(n => n.id === id);
        if (!known) dataRef.current.nodes.push({ id, label, key, name, pending });
        else if (pending !== undefined) known.pending = pending;
        return id;
    };

    const expandNode = async (label: string, key: string) => {
        const id = nid(label, key);
        if (fetchedRef.current.has(id)) return;
        fetchedRef.current.add(id);
        try {
            const r = await authFetch(`/knowledge/records/${encodeURIComponent(label)}/${encodeURIComponent(key)}`);
            if (!r.ok) return;
            const record: KnowledgeRecordDetail = await r.json();
            addNode(label, key, record.name || key, record.pending === 'new');
            for (const rel of record.relationships) {
                const otherId = addNode(rel.node.label, rel.node.key, rel.node.name);
                const source = rel.direction === 'out' ? id : otherId;
                const target = rel.direction === 'out' ? otherId : id;
                if (!dataRef.current.links.some(l => l.source === source && l.target === target && l.type === rel.type)) {
                    dataRef.current.links.push({ source, target, type: rel.type, pending: rel.pending });
                }
            }
            applyToGraph();
            rerender();
        } catch {}
    };

    const seedFrom = (record: KnowledgeRecord) => {
        addNode(seedLabel, record.key, record.name || record.key, record.pending === 'new');
        applyToGraph();
        expandNode(seedLabel, record.key);
    };

    onNodeClickRef.current = (label: string, key: string) => {
        onOpenNode?.(label, key);
        expandNode(label, key);
    };

    const clearGraph = () => {
        dataRef.current = { nodes: [], links: [] };
        fetchedRef.current = new Set();
        enabledRef.current = new Set();
        applyToGraph();
        rerender();
    };

    const toggleLabel = (lbl: string) => {
        if (enabledRef.current.size === 0) {
            enabledRef.current = new Set(dataRef.current.nodes.map(n => n.label));
        }
        if (enabledRef.current.has(lbl) && enabledRef.current.size > 1) enabledRef.current.delete(lbl);
        else enabledRef.current.add(lbl);
        applyToGraph();
        rerender();
    };

    useEffect(() => {
        authFetch('/knowledge/nodes').then(r => r.ok ? r.json() : []).then((data: KnowledgeLabelSummary[]) => {
            setLabels(data);
            if (data[0]) setSeedLabel(data[0].label);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!seedLabel) { setSeedNodes([]); return; }
        let stale = false;
        setLoadingSeeds(true);
        authFetch(`/knowledge/records/${encodeURIComponent(seedLabel)}`)
            .then(r => r.ok ? r.json() : { records: [] })
            .then((d: { records: KnowledgeRecord[] }) => { if (!stale) setSeedNodes(d.records || []); })
            .catch(() => { if (!stale) setSeedNodes([]); })
            .finally(() => { if (!stale) setLoadingSeeds(false); });
        return () => { stale = true; };
    }, [seedLabel]);

    useEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const graph = ForceGraph()(el)
            .width(el.clientWidth)
            .height(el.clientHeight)
            .backgroundColor('#F7FAFA')
            .nodeRelSize(4)
            .nodeColor((n: any) => labelColor(n.label))
            .nodeLabel((n: any) => `<div style="background:white;padding:6px 8px;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,0.15);font-size:12px;color:#2F3A3D;"><b>${n.name}</b><br/><span style="color:#5E6E73;">${n.label}${n.pending ? ' · pending review' : ''}</span></div>`)
            .nodeCanvasObjectMode(() => 'replace')
            .nodeCanvasObject((node: any, ctx: CanvasRenderingContext2D) => {
                const r = 4;
                ctx.beginPath();
                ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
                if (node.pending) {
                    // Not on the record yet: an outline, dashed.
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fill();
                    ctx.setLineDash([1.2, 0.8]);
                    ctx.lineWidth = 0.9;
                    ctx.strokeStyle = labelColor(node.label);
                    ctx.stroke();
                    ctx.setLineDash([]);
                } else {
                    ctx.fillStyle = labelColor(node.label);
                    ctx.fill();
                }
                const text = node.name && node.name.length > 18 ? node.name.slice(0, 16) + '…' : (node.name || '');
                const fontSize = 3.5;
                ctx.font = `${fontSize}px Sans-Serif`;
                const tw = ctx.measureText(text).width;
                ctx.fillStyle = 'rgba(255,255,255,0.85)';
                ctx.fillRect(node.x - tw / 2 - 0.5, node.y + r + 0.5, tw + 1, fontSize + 1);
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillStyle = '#2F3A3D';
                ctx.fillText(text, node.x, node.y + r + 1);
            })
            .linkColor(() => '#C7D4D6')
            .linkWidth(1)
            .linkDirectionalArrowLength(2.5)
            .linkDirectionalArrowRelPos(1)
            .linkLabel((l: any) => l.pending ? `${l.type} (pending review)` : l.type)
            .linkLineDash((l: any) => (l.pending ? [2, 1.5] : null))
            .onNodeClick((n: any) => onNodeClickRef.current(n.label, n.key))
            .cooldownTime(2000);
        graphRef.current = graph;

        // The container changes size with the window and with the agent panel beside it,
        // which animates its width, so the canvas follows the container itself.
        const onResize = () => graph.width(el.clientWidth).height(el.clientHeight);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize);
        if (observer) observer.observe(el);
        else window.addEventListener('resize', onResize);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', onResize);
            graph._destructor?.();
        };
    }, []);

    const filteredSeeds = seedNodes
        .filter(n => !seedFilter.trim() || (n.name || n.key).toLowerCase().includes(seedFilter.toLowerCase()))
        .sort((a, b) => (a.name || a.key).localeCompare(b.name || b.key));
    const seedLabels = [...labels].sort((a, b) => a.name.localeCompare(b.name));
    const labelsInGraph = Array.from(new Set(dataRef.current.nodes.map(n => n.label))).sort();
    const labelName = (l: string) => labels.find(x => x.label === l)?.name ?? labelText(l);

    return (
        <div className="flex flex-col md:flex-row gap-4">
            <div className="md:w-64 shrink-0 space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-coolslate/70 px-1">Add to graph</p>
                <select
                    value={seedLabel}
                    onChange={e => { setSeedLabel(e.target.value); setSeedFilter(''); }}
                    className="w-full px-3 py-2 text-sm border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand/40"
                >
                    {seedLabels.map(l => (
                        <option key={l.label} value={l.label}>{l.name} ({l.count}{l.pending ? `, ${l.pending} pending` : ''})</option>
                    ))}
                </select>
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-coolslate/50" />
                    <input
                        value={seedFilter}
                        onChange={e => setSeedFilter(e.target.value)}
                        placeholder="Filter…"
                        className="w-full pl-8 pr-3 py-2 text-[13px] border border-softgrey rounded-lg bg-white text-graphite placeholder-coolslate focus:outline-none focus:border-brand/40"
                    />
                </div>
                <div className="border border-softgrey rounded-lg divide-y divide-softgrey max-h-[48vh] overflow-y-auto">
                    {loadingSeeds ? (
                        <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-brand" /></div>
                    ) : filteredSeeds.length === 0 ? (
                        <p className="text-[12px] text-coolslate text-center py-6">No nodes.</p>
                    ) : filteredSeeds.map(n => (
                        <button
                            key={n.key}
                            onClick={() => seedFrom(n)}
                            className="w-full text-left px-3 py-2 text-[13px] text-graphite hover:bg-offwhite transition-colors truncate"
                            title={n.name || n.key}
                        >
                            {n.name || n.key}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex-1 relative bg-white border border-softgrey rounded-xl h-[68vh] overflow-hidden">
                <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

                <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
                    <div className="relative">
                        <button
                            onClick={() => setShowFilter(s => !s)}
                            className={`rounded-lg shadow-md p-2.5 transition-colors ${enabledRef.current.size > 0 ? 'bg-brand text-white' : 'bg-white text-graphite border border-softgrey hover:bg-offwhite'}`}
                            title="Filter by type"
                        >
                            <Filter className="w-4 h-4" />
                        </button>
                        {showFilter && (
                            <div className="absolute top-11 right-0 bg-white rounded-lg shadow-lg border border-softgrey p-3 min-w-52">
                                {labelsInGraph.length === 0 ? (
                                    <p className="text-[12px] text-coolslate text-center py-1">Nothing in the graph yet</p>
                                ) : labelsInGraph.map(lbl => {
                                    const count = dataRef.current.nodes.filter(n => n.label === lbl).length;
                                    const on = enabledRef.current.size === 0 || enabledRef.current.has(lbl);
                                    return (
                                        <label key={lbl} className="flex items-center gap-2.5 cursor-pointer hover:bg-offwhite p-1.5 rounded-md">
                                            <input type="checkbox" checked={on} onChange={() => toggleLabel(lbl)} className="w-3.5 h-3.5 text-brand rounded" />
                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: labelColor(lbl) }} />
                                            <span className="text-[13px] text-graphite flex-1">{labelName(lbl)}</span>
                                            <span className="text-[11px] text-coolslate">{count}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {dataRef.current.nodes.length > 0 && (
                        <button
                            onClick={clearGraph}
                            className="rounded-lg shadow-md p-2.5 bg-white text-state-error border border-softgrey hover:bg-offwhite transition-colors"
                            title="Clear graph"
                        >
                            <BrushCleaning className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {dataRef.current.nodes.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center px-6">
                            <Share2Placeholder />
                            <h3 className="text-base font-medium text-graphite mb-1">Build a graph</h3>
                            <p className="text-sm text-coolslate max-w-xs">
                                Pick a node from the left to drop it in, then click any node to expand its relationships.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Share2Placeholder() {
    return (
        <svg className="w-12 h-12 text-softgrey mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
    );
}
